import { useState, useRef } from "react";
import { supabase } from "@/lib/supabase";
import { QRCodeCanvas } from "qrcode.react";
import { jsPDF } from "jspdf";
import {
  buildCheckinUrl,
  generateCheckinToken,
  checkinWindowStatus,
} from "@/lib/checkin";

interface CheckinEvent {
  id: number;
  title: string;
  event_datetime: string;
  checkin_token: string | null;
  checkin_opens_at: string | null;
  checkin_closes_at: string | null;
}

type ToastFn = (type: "success" | "error", message: string) => void;

interface Props {
  event: CheckinEvent;
  onToast: ToastFn;
}

const EVENT_TIME_ZONE = "America/New_York";

function toDatetimeLocalNY(iso: string | null): string {
  if (!iso) return "";
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: EVENT_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(new Date(iso));
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? "00";
  const hour = get("hour") === "24" ? "00" : get("hour");
  return `${get("year")}-${get("month")}-${get("day")}T${hour}:${get("minute")}`;
}

function nyLocalToUtcIso(datetimeLocal: string): string {
  const [datePart, timePart] = datetimeLocal.split("T");
  const [year, month, day] = datePart.split("-").map(Number);
  const [hour, minute] = timePart.split(":").map(Number);

  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone: EVENT_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });

  let ts = Date.UTC(year, month - 1, day, hour, minute, 0);
  for (let i = 0; i < 2; i++) {
    const parts = formatter.formatToParts(new Date(ts));
    const getNum = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? "0");
    const zonedTs = Date.UTC(
      getNum("year"),
      getNum("month") - 1,
      getNum("day"),
      getNum("hour") % 24,
      getNum("minute"),
      getNum("second"),
    );
    ts += Date.UTC(year, month - 1, day, hour, minute, 0) - zonedTs;
  }
  return new Date(ts).toISOString();
}

function formatEventDate(iso: string): string {
  return new Date(iso).toLocaleDateString("pt-BR", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: EVENT_TIME_ZONE,
  });
}

function formatEventTime(iso: string): string {
  return new Date(iso).toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZone: EVENT_TIME_ZONE,
  });
}

export default function EventCheckin({ event, onToast }: Props) {
  const qrContainerRef = useRef<HTMLDivElement>(null);

  const [token, setToken] = useState<string | null>(event.checkin_token);
  const [opensAt, setOpensAt] = useState(toDatetimeLocalNY(event.checkin_opens_at));
  const [closesAt, setClosesAt] = useState(toDatetimeLocalNY(event.checkin_closes_at));
  const [savingWindow, setSavingWindow] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [confirmRegen, setConfirmRegen] = useState(false);

  const url = token ? buildCheckinUrl(event.id, token) : "";
  const windowStatus = checkinWindowStatus(event.checkin_opens_at, event.checkin_closes_at);

  function getQrDataUrl(): string | null {
    const canvas = qrContainerRef.current?.querySelector("canvas");
    if (!canvas) return null;
    return canvas.toDataURL("image/png");
  }

  function downloadPng() {
    const dataUrl = getQrDataUrl();
    if (!dataUrl) {
      onToast("error", "Não foi possível gerar a imagem.");
      return;
    }
    const a = document.createElement("a");
    a.href = dataUrl;
    a.download = `checkin-evento-${event.id}.png`;
    a.click();
  }

  function downloadPdf() {
    const dataUrl = getQrDataUrl();
    if (!dataUrl) {
      onToast("error", "Não foi possível gerar o cartão.");
      return;
    }

    const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a5" });
    const pageWidth = 148;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(17);
    doc.text(event.title, pageWidth / 2, 28, { align: "center" });

    doc.setFont("helvetica", "normal");
    doc.setFontSize(12);
    doc.text(`${formatEventDate(event.event_datetime)} às ${formatEventTime(event.event_datetime)}`, pageWidth / 2, 40, {
      align: "center",
    });

    doc.setFontSize(11);
    doc.text("Aponte a câmera para fazer seu check-in", pageWidth / 2, 52, { align: "center" });

    doc.addImage(dataUrl, "PNG", 24, 62, 100, 100);

    doc.save(`checkin-evento-${event.id}-cartao.pdf`);
  }

  function copyLink() {
    if (!url) return;
    navigator.clipboard.writeText(url).then(
      () => onToast("success", "Link de check-in copiado!"),
      () => onToast("error", "Não foi possível copiar o link."),
    );
  }

  async function saveWindow() {
    if (!opensAt || !closesAt) {
      onToast("error", "Defina o início e o fim da janela de check-in.");
      return;
    }
    setSavingWindow(true);
    const { error } = await supabase
      .from("upcoming_events")
      .update({
        checkin_opens_at: nyLocalToUtcIso(opensAt),
        checkin_closes_at: nyLocalToUtcIso(closesAt),
      })
      .eq("id", event.id);
    setSavingWindow(false);

    if (error) {
      onToast("error", "Falha ao salvar a janela: " + (error.message || ""));
    } else {
      onToast("success", "Janela de check-in atualizada.");
    }
  }

  async function regenerate() {
    setRegenerating(true);
    const newToken = generateCheckinToken();
    const { error } = await supabase
      .from("upcoming_events")
      .update({ checkin_token: newToken })
      .eq("id", event.id);
    setRegenerating(false);
    setConfirmRegen(false);

    if (error) {
      onToast("error", "Falha ao gerar novo código: " + (error.message || ""));
    } else {
      setToken(newToken);
      onToast("success", "Novo código gerado. O anterior foi invalidado.");
    }
  }

  return (
    <div>
      <h3 className="font-display text-lg text-brown mb-1">Check-in por QR code</h3>
      <p className="font-body text-sm text-brown/50 mb-6">
        {event.title}
      </p>

      {!token ? (
        <div className="bg-white rounded-2xl p-10 text-center">
          <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center">
            <i className="ri-qr-code-line text-2xl text-primary" />
          </div>
          <p className="font-body text-sm text-brown/60 mb-5">
            Este evento ainda não tem código de check-in.
          </p>
          <button
            onClick={regenerate}
            disabled={regenerating}
            className="px-5 py-2.5 rounded-full bg-primary text-white font-body text-sm hover:bg-primary-dark transition-colors disabled:opacity-60 whitespace-nowrap cursor-pointer"
          >
            {regenerating ? "Gerando..." : "Gerar código"}
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* QR + downloads */}
          <div className="bg-white rounded-2xl p-6 md:p-8 flex flex-col items-center">
            <div
              ref={qrContainerRef}
              className="p-4 bg-white rounded-2xl border-4 border-primary/10"
            >
              <QRCodeCanvas
                value={url}
                size={240}
                level="M"
                includeMargin={true}
                bgColor="#ffffff"
                fgColor="#000000"
              />
            </div>
            <p className="font-body text-xs text-brown/40 mt-3 text-center">
              Aponte a câmera do celular para abrir o check-in
            </p>

            <div className="flex flex-wrap justify-center gap-2 mt-5">
              <button
                onClick={downloadPng}
                className="px-4 py-2.5 rounded-full bg-primary text-white font-body text-sm hover:bg-primary-dark transition-colors whitespace-nowrap cursor-pointer"
              >
                <div className="w-4 h-4 inline-flex items-center justify-center mr-1.5">
                  <i className="ri-download-line" />
                </div>
                Baixar PNG
              </button>
              <button
                onClick={downloadPdf}
                className="px-4 py-2.5 rounded-full bg-secondary-100 text-secondary-900 font-body text-sm hover:bg-secondary-200 transition-colors whitespace-nowrap cursor-pointer"
              >
                <div className="w-4 h-4 inline-flex items-center justify-center mr-1.5">
                  <i className="ri-file-pdf-line" />
                </div>
                Baixar cartão (PDF)
              </button>
              <button
                onClick={copyLink}
                className="px-4 py-2.5 rounded-full bg-white border border-nude text-brown font-body text-sm hover:bg-beige transition-colors whitespace-nowrap cursor-pointer"
              >
                <div className="w-4 h-4 inline-flex items-center justify-center mr-1.5">
                  <i className="ri-link" />
                </div>
                Copiar link
              </button>
            </div>
          </div>

          {/* Configurações */}
          <div className="space-y-5">
            {/* Link escrito por extenso (plano B para o grupo) */}
            <div className="bg-white rounded-2xl p-5">
              <p className="font-body text-xs text-brown/50 uppercase tracking-wide mb-2">
                Link para colar no grupo (plano B)
              </p>
              <div className="flex items-center gap-2">
                <div className="flex-1 min-w-0 bg-beige/60 border border-nude rounded-lg px-3 py-2.5 font-mono text-xs text-brown break-all">
                  {url}
                </div>
                <button
                  onClick={copyLink}
                  className="shrink-0 w-10 h-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center hover:bg-primary/20 transition-colors cursor-pointer"
                  title="Copiar link"
                >
                  <i className="ri-file-copy-line" />
                </button>
              </div>
            </div>

            {/* Janela de validade */}
            <div className="bg-white rounded-2xl p-5">
              <p className="font-body text-xs text-brown/50 uppercase tracking-wide mb-1">
                Janela de validade do check-in
              </p>
              <p className="font-body text-xs text-brown/50 mb-4">
                {windowStatus === "open"
                  ? "Check-in aberto agora."
                  : windowStatus === "before"
                    ? "Check-in ainda não abriu."
                    : "Check-in encerrado."}
              </p>
              <div className="space-y-3">
                <div>
                  <label className="font-body text-sm text-brown block mb-1.5">Abre em</label>
                  <input
                    type="datetime-local"
                    value={opensAt}
                    onChange={(e) => setOpensAt(e.target.value)}
                    className="w-full px-4 py-2.5 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown focus:outline-none focus:border-primary transition-colors"
                  />
                </div>
                <div>
                  <label className="font-body text-sm text-brown block mb-1.5">Fecha em</label>
                  <input
                    type="datetime-local"
                    value={closesAt}
                    onChange={(e) => setClosesAt(e.target.value)}
                    className="w-full px-4 py-2.5 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown focus:outline-none focus:border-primary transition-colors"
                  />
                </div>
                <button
                  onClick={saveWindow}
                  disabled={savingWindow}
                  className="px-5 py-2.5 rounded-full bg-primary text-white font-body text-sm hover:bg-primary-dark transition-colors disabled:opacity-60 whitespace-nowrap cursor-pointer"
                >
                  {savingWindow ? "Salvando..." : "Salvar janela"}
                </button>
              </div>
            </div>

            {/* Gerar novo código */}
            <div className="bg-white rounded-2xl p-5 border border-amber-200 bg-amber-50/50">
              <p className="font-body text-sm text-brown font-semibold mb-1">
                O cartão vazou?
              </p>
              <p className="font-body text-xs text-brown/60 mb-3">
                Gerar um novo código invalida o anterior na hora.
              </p>
              {confirmRegen ? (
                <div className="flex items-center gap-2">
                  <button
                    onClick={regenerate}
                    disabled={regenerating}
                    className="px-4 py-2.5 rounded-full bg-amber-500 text-white font-body text-sm hover:bg-amber-600 transition-colors disabled:opacity-60 whitespace-nowrap cursor-pointer"
                  >
                    {regenerating ? "Gerando..." : "Confirmar — invalidar código atual"}
                  </button>
                  <button
                    onClick={() => setConfirmRegen(false)}
                    className="px-4 py-2.5 rounded-full bg-white border border-nude text-brown font-body text-sm hover:bg-beige transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Voltar
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setConfirmRegen(true)}
                  className="px-4 py-2.5 rounded-full bg-white border border-amber-300 text-amber-700 font-body text-sm hover:bg-amber-50 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <div className="w-4 h-4 inline-flex items-center justify-center mr-1.5">
                    <i className="ri-refresh-line" />
                  </div>
                  Gerar novo código
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}